import Logger from '@ffw/logger-lib';

const initDataLayerObject = (pageType, locationRef) => {
  let env = process.env.NODE_ENV;
  switch (env) {
    case 'development':
      env = 'DEV';
      break;
    case 'test':
    case 'tst2':
      env = 'QA';
      break;
    case 'production':
    case 'prod':
    case 'prd2':
      env = 'PROD';
      break;
    default:
      env = 'DEV';
  }
  let breadcrumb = [];
  if (typeof window !== 'undefined') {
    // Store the current page type in sessionStorage and load the previous ones.
    breadcrumb = JSON.parse(sessionStorage.getItem('breadcrumb')) || [];
    sessionStorage.setItem('breadcrumb', JSON.stringify(breadcrumb));
  }
  return {
    environment: env.toUpperCase(),
    country: process.env.REACT_APP_COUNTRY.toUpperCase(),
    type: pageType,
    breadcrumb: breadcrumb,
    originalLocation: locationRef,
    search_results: {
      search_result_amount: 0,
      search_result_distance: 0,
      search_result_page: 0,
      search_result_zip_code: '',
      search_result_keyword: '',
    },
  };
};

/**
 * Returns Salary Page datalayer page object.
 * NOTE: relatedJobs will be mutated for having the dataLayer properties.
 *
 * @param {{}} data
 *   Page route data.
 * @param {string} pageType
 *   Page type.
 * @param {string} locationRef
 *   Location reference.
 * @param {string} locale
 *   App locale.
 * @return {{}}
 *   Object ready for usage with dataLayer.
 */
const handleSalaryPage = (data, pageType = 'salary_details', locationRef, locale = process.env.REACT_APP_DEFAULT_LANGUAGE) => {
  return {
    page: {
      ...initDataLayerObject(pageType, locationRef),
      language: locale,
    },
    ecommerce: {
      impressions: (data?.relatedJobs?.results || []).map((job, index) => {
        // Note: job is mutable parseJobInfo() adds job.dataLayer*() callbacks
        // to job objects by reference.
        return parseJobInfo(job, (index + 1), 'related');
      }),
    },
  };
};

// Helper for formatting numbers.
const formatNumber = (number) => {
  // Convert possible string value to number.
  number = parseFloat(number);
  // Fix it to 2 digits and again parse it to number.
  return parseFloat(number.toFixed(2));
};

/**
 * Helper function that returns datalayer object for a job.
 * Mutates job object and adds datalayer properties.
 *
 * @param {{}} job
 *   RUSA API Job object.
 * @param {Number} position
 *   The position in the list the job in which is loaded.
 * @param {string} listName
 *   The name of the list the job in which is loaded.
 * @param {string|false} applicationId
 *   Application ID.
 * @return {{}|false}
 */
const parseJobInfo = (job, position = 0, listName = '', applicationId = false) => {
  if (!job) {
    Logger.info('Missing job info when building datalayer.', 'dataLayerHelper');
    return false;
  }

  const info = {
    'application_id': applicationId,
    'job_id': job.id,
    'job_title': job.title,
    'category': job.categories?.join(', '),
    'application_type': 'direct application',
    'operating_company': job.lobName ?? '',
    'position': position,
    'list_name': listName,
    'standardized_job_code': job.sicCode?.join(', ') ?? '',
    'standardized_job_title': '',
    'industry': job.industries?.join(', ') ?? '',
    'hours': job.workHours?.join(', ') ?? null,
    'minimum_salary': (job.salary && job.salary.min && job.salary.type) ?
      formatNumber(getAnnualSalary(Number(job.salary.min), job.salary.type)) : 0,
    'maximum_salary': (job.salary && job.salary.max && job.salary.type) ?
      formatNumber(getAnnualSalary(Number(job.salary.max), job.salary.type)) : 0,
    'employment_type': getEmploymentType(job.type, job.employmentType),
    'education_level': job.educationRequirements?.join(', ') ?? '',
    'work_experience': job.experienceRequirements?.join(', ') ?? '',
    'internal': job.isInternal ? (job.isInternal === 'true') : false,
    'zip_code': job.jobLocation?.postalCode ?? '',
    'city': job.jobLocation?.city ?? '',
    'region': job.jobLocation?.state ?? '',
    'business_unit': job.officeLocation?.businessUnit ?? '',
    'branch_name': job.officeLocation.address1 ? job.officeLocation.address1 : job.lobName,
    'branch_zip_code': job.officeLocation?.postalCode ?? '',
    'branch_city': job.officeLocation?.city ?? '',
    'branch_region': job.officeLocation?.state ?? '',
    'launch_date': formatDateToISOString(new Date(job.createdDate)),
    'url': `${process.env.REACT_APP_BASE_URL}${job.JobUrl}`,
    'skills': job.skills?.join(', '),
    'reference_number': job.atsReference,
  };

  // Add jobClick event.
  job.dataLayerJobClick = push({
    'event': 'jobClick',
    'ecommerce': {
      'click': {
        'list': listName,
        'jobs': [info],
      },
    },
  });

  return info;
};

const getEmploymentType = (empType, eType) => {
  eType = !eType ? eType : eType.toLowerCase();
  if ((!eType || eType === 'full-time') && ((empType === 'Permanent') || (empType === 'Work from home - Permanent'))) {
    return 'FULL_TIME';
  } else if ((!eType || eType === 'part-time') && ((empType === 'Permanent') || (empType === 'Work from home - Permanent'))) {
    return 'PART_TIME';
  } else if ((!eType || eType === 'full-time') && (empType === 'Contract' || empType === 'Work from home - Contract')) {
    return '["CONTRACTOR","FULL_TIME"]';
  } else if ((!eType ||eType === 'part-time') && (empType === 'Contract' || empType === 'Work from home - Contract')) {
    return '["CONTRACTOR","PART_TIME"]';
    // eslint-disable-next-line max-len
  } else if ((!eType || eType === 'full-time') && (empType === 'Temporary' || empType === 'Interim' || empType === 'Temp to Perm')) {
    return '["TEMPORARY","FULL_TIME"]';
    // eslint-disable-next-line max-len
  } else if ((!eType || eType === 'part-time') && (empType === 'Temporary' || empType === 'Interim' || empType === 'Temp to Perm')) {
    return '["TEMPORARY","PART_TIME"]';
  } else if (empType === 'Voluntary') {
    return 'VOLUNTEER';
  } else if (empType === 'Training' || empType === 'Internship') {
    return 'INTERN';
  } else {
    return 'OTHER';
  }
};

/**
 * Helper to calculate annual salary.
 *
 * Note: All calculations will be done with following assumptions:
 *  - 8 working hours per day;
 *  - 5 working hours per week;
 *  - 52 weeks per year.
 *
 * @param {Number} value
 *   Salary value.
 * @param {'per hour'|'per day'|'per week'} compensationType
 *   Salary type.
 * @return {Number}
 *   Annual salary value.
 */
const getAnnualSalary = (value, compensationType) => {
  switch (compensationType) {
    case 'per hour':
      return value * 2080; // 8 h/day * 5 days/week * 52 week/year.
    case 'per day':
      return value * 260; // 5 days/week * 52 week/year.
    case 'per week':
      return value * 52; // 52 week/year.
    default:
      return value;
  }
};

// Helper function to format date in ISO format.
// The toISOString() date method uses milliseconds, which we don't need.
const formatDateToISOString = (date, hasTime = false) => {
  // Make sure the date is valid.
  if (isNaN(date.getMonth())) {
    return false;
  }
  const pad = (number) => (number < 10) ? '0' + number : number;
  return hasTime ?
    date.getUTCFullYear() +
    '-' + pad(date.getUTCMonth() + 1) +
    '-' + pad(date.getUTCDate()) +
    'T' + pad(date.getUTCHours()) +
    ':' + pad(date.getUTCMinutes()) +
    ':' + pad(date.getUTCSeconds()) +
    'Z' :
    date.getUTCFullYear() +
    '-' + pad(date.getUTCMonth() + 1) +
    '-' + pad(date.getUTCDate());
};

const push = (data, key = false, subKey = false) => {
  return (key !== false && subKey !== false) ?
    `window.dataLayer = window.dataLayer || []; window.dataLayer[${key}].${subKey} = data.${subKey};` :
    `window.dataLayer = window.dataLayer || []; let newData = ${JSON.stringify(data)}; window.dataLayer.push(newData);`;
};

export {
  handleSalaryPage,
};
